import React, { useMemo } from 'react';
import { Col, Container, Modal, ModalBody, Row } from 'react-bootstrap';
import ModalHeader from '../ModalHeader';
import Label from '../Label';
import Button from '../Button';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import Blockquote from '../Blockquote';
import {
  addOrRemoveMonthsToGivenDate,
  DateFormats,
  formatDate,
  formatPrice,
  getFirstDayOfMonth,
  getLastDayOfMonth,
} from '../../utils/utils';
import useMobile from '../../hooks/useMobileHook';

export type PaymentInformationStatementModalProps = React.PropsWithoutRef<{
  showModal: boolean;
  handleClose: () => void;
  dateConfig: {
    month: number;
    year: number;
  };
}>;

const generatePayments = (startDate: Date) => {
  return [
    {
      postDate: formatDate(
        addOrRemoveDaysToDate(addOrRemoveMonthsToGivenDate(startDate, -1), 21),
        DateFormats.US
      ),
      description: 'Charge',
      benefit: 'Medical Plan',
      amount: 212.0,
    },
    {
      postDate: formatDate(
        addOrRemoveDaysToDate(addOrRemoveMonthsToGivenDate(startDate, -2), 21),
        DateFormats.US
      ),
      description: 'Charge',
      benefit: 'Dental Plan',
      amount: 50.0,
    },
    {
      postDate: formatDate(
        addOrRemoveDaysToDate(addOrRemoveMonthsToGivenDate(startDate, -3), 21),
        DateFormats.US
      ),
      description: 'Charge',
      benefit: 'Medical Plan - Medicare',
      amount: 70.0,
    },
  ];
};

const addOrRemoveDaysToDate = (date: Date, daysToAdd: number) => {
  return new Date(new Date(date).setDate(new Date(date).getDate() + daysToAdd));
};

const PaymentInformationStatementModal = ({
  showModal = false,
  handleClose,
  dateConfig,
  ...props
}: PaymentInformationStatementModalProps) => {
  const payments = useMemo(
    () => generatePayments(new Date(dateConfig.year, dateConfig.month, 1)),
    []
  );

  const isMobile = useMobile();

  return (
    <Modal show={showModal} onHide={handleClose} {...props} size="xl">
      <ModalHeader closeButton>
        <h5>Statement/Receipt</h5>
      </ModalHeader>
      <ModalBody className="pt-9">
        <Container>
          <Row>
            <Col className="border rounded-2 col-12 p-10">
              <Row>
                <Col className="col-lg-9 col-12 ">
                  <Label className={'p-0 m-0 mb-2'}>
                    <span className={'lh-xs'}>Terry Smith (111111111)</span>
                  </Label>
                  <Label className={'p-0 m-0'}>
                    <span className={'lh-xs p-0 m-0 text-dark-gray-50'}>
                      {' '}
                      375 Oxford St.
                    </span>
                  </Label>
                  <Label className={'p-0 m-0'}>
                    <span className={'lh-xs p-0 text-dark-gray-50'}>
                      {' '}
                      Straton, CA 40500
                    </span>
                  </Label>
                  <Label className={'p-0 m-0'}>
                    <span className={'lh-xs p-0 text-dark-gray-50'}>
                      {' '}
                      (555) 112-1234
                    </span>
                  </Label>
                </Col>
                <Col className="col-lg-3 col-12 mt-10 mt-lg-0">
                  <Button
                    variant="primary"
                    leftIcon={light('file-download')}
                    className={'w-100'}
                  >
                    Download/Print
                  </Button>
                </Col>
              </Row>
              <Row className="mt-8">
                <Col className={'col-lg-6 col-12 pe-10'}>
                  <Row>
                    <Col>
                      <Row className="text-fs-6 fw-light lh-xs pt-3 pb-3 px-3">
                        <Col className="col-12 ps-0 border-bottom pb-3">
                          Transaction Information
                        </Col>
                      </Row>
                      <Row className="text-fs-6 fw-light lh-xs pt-3 pb-3 px-3">
                        <Col className="col-8 ps-0 text text-dark-gray-50 ">
                          Account Number{' '}
                        </Col>

                        <Col className="col-4 text-end pe-0">123456789</Col>
                      </Row>
                      <Row className="text-fs-6 fw-light lh-xs pt-3 pb-3 px-3">
                        <Col className="col-8 ps-0 text text-dark-gray-50 ">
                          Payment Due Date{' '}
                        </Col>

                        <Col className="col-4 text-end pe-0">
                          {formatDate(
                            getFirstDayOfMonth(
                              dateConfig.year,
                              dateConfig.month
                            ),
                            DateFormats.SHORT_MONTH_DAY_YEAR
                          )}
                        </Col>
                      </Row>
                      <Row className="text-fs-6 fw-light lh-xs pt-3 pb-3 px-3">
                        <Col className="col-4 ps-0 teFxt text-dark-gray-50 ">
                          Coverage Period
                        </Col>

                        <Col className="col-8 text-end pe-0">
                          {`${formatDate(
                            getFirstDayOfMonth(
                              dateConfig.year,
                              dateConfig.month
                            ),
                            DateFormats.US
                          )} - ${formatDate(
                            getLastDayOfMonth(
                              dateConfig.year,
                              dateConfig.month
                            ),
                            DateFormats.US
                          )} `}
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
                <Col className={'col-lg-6 col-12 ps-10'}>
                  <Row>
                    <Col>
                      <Row className="text-fs-6 fw-light lh-xs pt-3 pb-3 px-3  ">
                        <Col className="col-12 ps-0 border-bottom pb-3">
                          Payment Information
                        </Col>
                      </Row>
                      <Row className="text-fs-6 fw-light lh-xs pt-3 pb-3 px-3">
                        <Col className="col-8 ps-0 text text-dark-gray-50 ">
                          Amount Due
                        </Col>

                        <Col className="col-4 text-end pe-0">$0.00</Col>
                      </Row>
                      <Row className="text-fs-6 fw-light lh-xs pt-3 pb-3 px-3">
                        <Col className="col-8 ps-0 text text-dark-gray-50 ">
                          Received
                        </Col>

                        <Col className="col-4 text-end pe-0">
                          {formatDate(
                            addOrRemoveDaysToDate(
                              getFirstDayOfMonth(
                                dateConfig.year +
                                  (dateConfig.month === 1 ? -1 : 0),
                                dateConfig.month === 1
                                  ? 12
                                  : dateConfig.month - 1
                              ),
                              17
                            ),

                            DateFormats.SHORT_MONTH_DAY_YEAR
                          )}
                        </Col>
                      </Row>
                      <Row className="text-fs-6 fw-light lh-xs pt-3 pb-3 px-3">
                        <Col className="col-8 ps-0 text text-dark-gray-50 ">
                          Payment Method
                        </Col>

                        <Col className="col-4 text-end pe-0">Credit Card</Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className={'mt-12 mb-12 '}>
            <Col className={'col-12 m-0 p-0 w-100'}>
              <Blockquote>
                <span className="text-dark-gray">
                  Thank you for your payment!
                </span>
              </Blockquote>
            </Col>
          </Row>
          <Row>
            <Col className={'col-12 overflow-auto'}>
              <Row
                className={'overflow-hidden'}
                style={{
                  minWidth: '650px',
                }}
              >
                <Col>
                  <Row className="text-fs-6 fw-light pt-3 lh-xs border-bottom px-3 pb-6">
                    <Col className="text-start fw-normal pe-0 col-lg-2 col-2 ">
                      Post Date
                    </Col>
                    <Col className="text-start fw-normal pe-0 col-lg-2 col-3 ">
                      Description
                    </Col>
                    <Col className="text-start fw-normal pe-0 col-lg-6 col-5 ">
                      Benefit
                    </Col>
                    <Col className="text-end fw-normal pe-0 col-lg-2 col-2 pe-7 ">
                      Amount
                    </Col>
                  </Row>

                  {payments.map((payment, index) => {
                    return (
                      <Row
                        className="text-fs-6 fw-light lh-xs pt-3 pb-3 px-3 border-bottom"
                        key={index}
                      >
                        <Col className="col-lg-2 pe-0 col-2 text-start  pt-2">
                          {payment.postDate}
                        </Col>
                        <Col className="col-lg-2 pe-0 col-3 text-start  pt-2">
                          {payment.description}
                        </Col>
                        <Col className="col-lg-6 pe-0 col-5 text-start  pt-2">
                          {payment.benefit}
                        </Col>
                        <Col className="col-lg-2 text-end pe-7 col-2  pt-2">
                          {formatPrice(Number(payment.amount), 'en-US', {
                            currency: 'USD',
                            style: 'currency',
                            minimumFractionDigits: 0,
                          })}
                        </Col>
                      </Row>
                    );
                  })}
                </Col>
              </Row>
              <Row className="justify-content-end mt-10 text-end">
                <Col
                  className={`col-lg-2 col-12 gray-nested-background ${
                    isMobile ? 'text-start' : 'text-end'
                  } pt-7 pb-7 pe-7`}
                >
                  <span className={'lh-xs text-end w-100  fw-normal'}>
                    Total Paid
                  </span>
                  <h4 className={'lh-xs'}>
                    {formatPrice(
                      Number(
                        payments.reduce((sum, { amount }) => sum + amount, 0)
                      ),
                      'en-US',
                      {
                        currency: 'USD',
                        style: 'currency',
                        minimumFractionDigits: 2,
                      }
                    )}
                  </h4>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="justify-content-center mb-12 mt-12 ">
            <Col className="col-lg-4 col-12">
              <Button
                variant={'primary-outline'}
                onClick={handleClose}
                className={'w-100'}
              >
                Close
              </Button>
            </Col>
          </Row>
        </Container>
      </ModalBody>
    </Modal>
  );
};

export default PaymentInformationStatementModal;
